import React, { useEffect, useState } from 'react';
import {
  addB2BShipment,
  getB2BOrder,
  updateB2BShipment,
} from '../../services/shipturtleApi';
import { showToast } from '../../utils/toast';
import './B2BOrders.css';

/**
 * Stage 2 — what ops records once the consignment is booked.
 *
 * A modal rather than a panel buried in the order, so it can be reached from
 * the list as well as the detail view: adding an AWB is the single most common
 * thing ops does here, and making them open an order first was friction for no
 * gain.
 *
 * Prozo does not send tracking for PTL shipments (verified against production
 * logs), so `progress` is typed by hand. The dropdown offers the subset that
 * can actually happen to a truck while the column stores the full parcel
 * vocabulary, so enabling automatic tracking later changes nothing here.
 */
const PROGRESS_OPTIONS = [
  ['', 'Not set'],
  ['order_placed', 'Order placed'],
  ['picked_up', 'Picked up'],
  ['in_transit', 'In transit'],
  ['out_for_delivery', 'Out for delivery'],
  ['delivered', 'Delivered'],
  ['rto', 'RTO'],
  ['shipment_delayed', 'Delayed'],
];

const EMPTY = {
  awb_number: '',
  tracking_link: '',
  shipper: '',
  expected_delivery_date: '',
  b2b_shipping_charges: '',
  charges_incurred: '',
  progress: '',
  remarks: '',
};

/**
 * @param shipment  The shipment to edit, when the caller already has it.
 * @param editOnly  Edit the order's only shipment, fetching it here. Lets the
 *                  list open this without carrying full shipment objects in
 *                  every row.
 */
export default function B2BShipmentModal({
  order,
  shipment = null,
  editOnly = false,
  onClose,
  onSaved,
}) {
  const [target, setTarget] = useState(shipment);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(editOnly && !shipment);
  const [error, setError] = useState('');

  const isEdit = Boolean(target);

  // Fetch when the caller could only say "this order's shipment" — the list
  // row carries a summary, not the record.
  useEffect(() => {
    if (!editOnly || shipment) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getB2BOrder(order.id);
        const first = (data?.order?.shipments || [])[0] || null;
        if (cancelled) return;
        if (!first) {
          setError('This order no longer has a shipment to edit.');
        }
        setTarget(first);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Could not load the shipment.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editOnly, shipment, order.id]);

  useEffect(() => {
    if (!target) {
      setForm(EMPTY);
      return;
    }
    setForm({
      awb_number: target.awb_number || '',
      tracking_link: target.tracking_link || '',
      shipper: target.shipper || '',
      expected_delivery_date: target.expected_delivery_date || '',
      b2b_shipping_charges: target.b2b_shipping_charges ?? '',
      charges_incurred: target.charges_incurred ?? '',
      progress: target.progress || '',
      remarks: target.remarks || '',
    });
  }, [target]);

  // Close on Escape, the way every other modal in the terminal behaves.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const set = (name) => (e) => setForm((prev) => ({ ...prev, [name]: e.target.value }));

  const valid = form.awb_number.trim() && form.tracking_link.trim() && form.shipper.trim();

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      // Empty strings on optional numerics would fail validation; drop them.
      ['b2b_shipping_charges', 'charges_incurred'].forEach((k) => {
        if (payload[k] === '') delete payload[k];
      });

      if (isEdit) {
        await updateB2BShipment(target.id, payload);
        showToast({ type: 'success', message: 'Shipment updated.' });
      } else {
        await addB2BShipment(order.id, payload);
        showToast({ type: 'success', message: `Shipment added to ${order.order_number}.` });
      }
      onSaved?.();
      onClose();
    } catch (err) {
      const message = err?.message || 'Could not save the shipment.';
      setError(message);
      showToast({ type: 'error', message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="b2bModalBackdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div
        className="b2bModalPanel b2bModalNarrow"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="b2bModalHead">
          <div>
            <h3>
              {loading ? 'Shipment' : isEdit ? 'Edit shipment' : 'Add shipment'}
            </h3>
            <span className="b2bModalSub">{order.order_number}</span>
          </div>
          <button type="button" className="b2bModalClose" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="b2bModalBody">
          {loading && <p className="b2bMuted">Loading shipment…</p>}

          {!loading && (
          <>
          <div className="b2bFormGrid">
            <label className="field">
              <span>
                AWB number <span className="b2bReq">*</span>
              </span>
              <input value={form.awb_number} onChange={set('awb_number')} />
            </label>
            <label className="field">
              <span>
                Shipper <span className="b2bReq">*</span>
              </span>
              <input
                value={form.shipper}
                onChange={set('shipper')}
                placeholder="e.g. DELHIVERY B2B, Porter"
              />
            </label>
          </div>

          <label className="field">
            <span>
              Tracking link <span className="b2bReq">*</span>
            </span>
            <input
              value={form.tracking_link}
              onChange={set('tracking_link')}
              placeholder="https://…"
            />
          </label>

          <div className="b2bFormGrid">
            <label className="field">
              <span>Expected delivery</span>
              <input
                value={form.expected_delivery_date}
                onChange={set('expected_delivery_date')}
                placeholder="e.g. 3-5 days, or a date"
              />
            </label>
            <label className="field">
              <span>Status</span>
              <select value={form.progress} onChange={set('progress')}>
                {PROGRESS_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <small className="helpText">Set by hand — PTL does not auto-track.</small>
            </label>
          </div>

          <div className="b2bFormGrid">
            <label className="field">
              <span>B2B shipping charges</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.b2b_shipping_charges}
                onChange={set('b2b_shipping_charges')}
              />
            </label>
            <label className="field">
              <span>Charges incurred</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.charges_incurred}
                onChange={set('charges_incurred')}
              />
            </label>
          </div>

          <label className="field">
            <span>Remarks</span>
            <textarea rows={2} value={form.remarks} onChange={set('remarks')} />
          </label>

          </>
          )}

          {error && <p className="fieldError">{error}</p>}
        </div>

        <footer className="b2bModalFoot">
          <button type="button" className="btn btnGhost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btnPrimary"
            disabled={!valid || saving || loading || (editOnly && !target)}
            onClick={handleSave}
          >
            {saving ? 'Saving…' : isEdit ? 'Save shipment' : 'Add shipment'}
          </button>
        </footer>
      </div>
    </div>
  );
}
